import React, { useEffect, useRef, useState } from "react";
import { WhatsappLogo, PaperPlaneTilt, MicrophoneStage } from "@phosphor-icons/react";

/**
 * HeroMessaggiMockup — versione minimal r132
 *
 * 4 step sequenziali (uno per volta, niente sovrapposizioni):
 *   A. Template UNICO scritto (3.0s) — esempio marketing "nuovo annuncio"
 *   B. Lista personalizzata 4 contatti (3.0s) — slide-in stagger
 *   C. WhatsApp si apre col messaggio per Luca (2.5s) — bottone Invia verde
 *   D. Categorie pronte (5.0s) — tag cliccabili che pulsano uno per volta
 *
 * Tono asciutto. Niente catalogo a scroll, niente 12 template.
 */

const RECIPIENTS = [
  { name: "Andrea",  zona: "Brera",       avatar: "MR", color: "#C8A66B" },
  { name: "Luca",   zona: "Isola",       avatar: "LV", color: "#7C8B6E" },
  { name: "Anna",   zona: "Porta Romana", avatar: "AB", color: "#A8755B" },
  { name: "Giulia", zona: "Navigli",     avatar: "GP", color: "#5A6A4D" },
];

const CATEGORIES = [
  { tag: "Auguri",          emoji: "🎄" },
  { tag: "Nuovo annuncio",  emoji: "🔑" },
  { tag: "Ribasso prezzo",  emoji: "💶" },
  { tag: "Open house",      emoji: "🎟️" },
  { tag: "Valutazione",     emoji: "📊" },
  { tag: "Follow-up visita", emoji: "✨" },
];

export default function HeroMessaggiMockup({ reelMode = false } = {}) {
  const rootRef = useRef(null);
  const [visible, setVisible] = useState(true);
  const [phase, setPhase] = useState("A"); // A | B | C | D
  const [revealed, setRevealed] = useState(0); // contatti apparsi 0..4
  const [catShown, setCatShown] = useState(0); // categorie apparse 0..6

  useEffect(() => {
    if (!rootRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }),
      { threshold: 0.25 },
    );
    obs.observe(rootRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let mounted = true;
    const tids = [];
    // Iter v29 r132 — il reel mostra cover+descr per i primi ~6s, poi entra
    // la fase DEMO. Aspettiamo 5s prima di animare il mockup così tutte le
    // 4 fasi sono visibili durante il DEMO window (~6.5s → 15s).
    const START_DELAY = reelMode ? 5000 : 0;
    // Fase A → B (dopo 2.5s di lettura del template)
    tids.push(setTimeout(() => mounted && setPhase("B"), START_DELAY + 2500));
    // Stagger contatti
    [0, 1, 2, 3].forEach((i) => {
      tids.push(setTimeout(() => mounted && setRevealed((r) => Math.max(r, i + 1)), START_DELAY + 2700 + i * 400));
    });
    // Fase B → C
    tids.push(setTimeout(() => mounted && setPhase("C"), START_DELAY + 4800));
    // Fase C → D (solo reelMode)
    if (reelMode) {
      tids.push(setTimeout(() => mounted && setPhase("D"), START_DELAY + 6800));
      // Categorie una per volta · 6 × 380ms = 2.3s
      CATEGORIES.forEach((_, i) => {
        tids.push(setTimeout(() => mounted && setCatShown((c) => Math.max(c, i + 1)), START_DELAY + 7000 + i * 380));
      });
    }
    return () => {
      mounted = false;
      tids.forEach((t) => clearTimeout(t));
    };
  }, [visible, reelMode]);

  return (
    <div ref={rootRef} className="max-w-[440px] mx-auto relative px-4" data-testid="hero-messaggi-mockup">
      <style>{`
        @keyframes msg-fade-in { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }
        @keyframes msg-fade-out { from { opacity: 1 } to { opacity: 0 } }
        @keyframes msg-pulse-once {
          0%, 100% { box-shadow: 0 0 0 0 rgba(124,139,110,0); }
          40% { box-shadow: 0 0 0 10px rgba(124,139,110,0); }
        }
        @keyframes msg-pop {
          0% { transform: scale(0.9); opacity: 0; }
          60% { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes msg-typing {
          0% { width: 0; }
          100% { width: 100%; }
        }
        @keyframes msg-cat-pulse {
          0% { background-color: rgba(124,139,110,0.18); transform: scale(0.94); opacity: 0; }
          40% { background-color: rgba(124,139,110,0.18); transform: scale(1.02); opacity: 1; }
          100% { background-color: rgba(255,255,255,1); transform: scale(1); opacity: 1; }
        }
        .phase-enter { animation: msg-fade-in 600ms ease-out both; }
        .phase-card { animation: msg-pulse-once 1.6s ease-out 300ms 1; }
      `}</style>

      {/* ── A · Template scritto una volta ─────────────────────── */}
      {phase === "A" && (
        <div className="phase-enter">
          <Eyebrow n={1} title="Scrivi il messaggio." />
          <div className="phase-card bg-white rounded-xl border border-[#EAE7DF] p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2.5 text-[10px] uppercase tracking-[0.2em] text-[#7C8B6E] font-bold">
              <MicrophoneStage size={12} weight="fill" />
              Detti a voce
            </div>
            <div className="text-[13px] text-[#1C1C1A] leading-relaxed">
              "Ciao <Tok>{"{nome}"}</Tok>, è uscito un trilocale a <Tok>{"{zona}"}</Tok> a 380.000. Te lo mando?"
            </div>
          </div>
          <div className="mt-3 text-[10.5px] text-[#7C8B6E] font-medium text-center">
            <span className="inline-block bg-[#F1F4ED] rounded-full px-3 py-1 border border-[#D9DDD0]">
              Una volta sola.
            </span>
          </div>
        </div>
      )}

      {/* ── B · 4 contatti personalizzati ───────────────────────── */}
      {phase === "B" && (
        <div className="phase-enter">
          <Eyebrow n={2} title="Diventa diverso per ognuno." />
          <div className="space-y-2">
            {RECIPIENTS.map((r, i) => (
              <div
                key={r.name}
                className="bg-white rounded-lg border border-[#EAE7DF] px-3 py-2.5 flex items-center gap-3"
                style={{
                  animation: revealed > i ? `msg-fade-in 500ms ease-out both` : undefined,
                  opacity: revealed > i ? 1 : 0,
                }}
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0"
                  style={{ backgroundColor: r.color }}
                >
                  {r.avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold text-[#1C1C1A] mb-0.5">{r.name} Ferrari</div>
                  <div className="text-[11px] text-[#3F3D38] leading-snug truncate">
                    Ciao <strong className="text-[#5A6A4D]">{r.name}</strong>, trilocale a <strong className="text-[#5A6A4D]">{r.zona}</strong>…
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── C · WhatsApp si apre ────────────────────────────────── */}
      {phase === "C" && (
        <div className="phase-enter">
          <Eyebrow n={3} title="WhatsApp si apre. Parti tu." />
          <div className="bg-[#1C1C1A] rounded-xl p-3 flex items-center gap-3 shadow-md">
            <div className="w-11 h-11 rounded-full bg-[#7C8B6E] flex items-center justify-center text-[12px] font-bold text-white shrink-0">
              LV
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] text-white font-semibold">Andrea Ferrari</div>
              <div className="text-[11px] text-white/70 italic truncate">"Ciao Luca, trilocale a Isola a 380.000…"</div>
            </div>
            <button
              type="button"
              className="bg-[#25D366] text-white text-[11px] font-bold px-3 py-2 rounded-md flex items-center gap-1 shrink-0"
              style={{ animation: "msg-pop 500ms cubic-bezier(.2,.7,.2,1) 400ms both" }}
              tabIndex={-1}
            >
              <PaperPlaneTilt size={12} weight="fill" /> Invia
            </button>
          </div>
          <div className="mt-3 text-[11px] text-[#5A6A4D] font-medium text-center leading-snug">
            <WhatsappLogo size={13} weight="fill" className="inline -mt-0.5 mr-1" />
            Dal tuo numero. Non da app.
          </div>
        </div>
      )}

      {/* ── D · Categorie pronte (solo reelMode) ─────────────────── */}
      {reelMode && phase === "D" && (
        <div className="phase-enter">
          <Eyebrow n={4} title="Non solo Natale. Tutto pronto." />
          <div className="grid grid-cols-2 gap-2">
            {CATEGORIES.map((c, i) => (
              <div
                key={c.tag}
                className="bg-white rounded-lg border border-[#EAE7DF] px-3 py-3 flex items-center gap-2"
                style={{
                  animation: catShown > i ? `msg-cat-pulse 900ms ease-out both` : undefined,
                  opacity: catShown > i ? 1 : 0,
                }}
              >
                <span className="text-[16px] leading-none shrink-0">{c.emoji}</span>
                <span className="text-[11.5px] font-semibold text-[#1C1C1A] leading-tight">{c.tag}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Eyebrow({ n, title }) {
  return (
    <div className="flex items-baseline gap-2 mb-3">
      <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#7C8B6E] tabular-nums">0{n}</span>
      <h4 className="text-[14px] font-semibold text-[#1C1C1A] leading-snug">{title}</h4>
    </div>
  );
}

function Tok({ children }) {
  return (
    <span className="font-semibold text-[#5A6A4D] bg-[#F1F4ED] px-1.5 py-0.5 rounded">
      {children}
    </span>
  );
}
